import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminCategoriesApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Category } from '../types';
import './AdminCategoryEditPage.css';

export const AdminCategoryEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { tg } = useTelegram();

  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

   
   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadCategory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCategory = async () => {
    if (!id) {
      setError('ID категории не указан');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get all categories and find the one we need
      const response = await adminCategoriesApi.getCategories();
      const foundCategory = response.categories.find((c) => c.id === parseInt(id));

      if (!foundCategory) {
        setError('Категория не найдена');
        return;
      }

      setCategory(foundCategory);
      setName(foundCategory.name);
      setDescription(foundCategory.description);
      setIsActive(foundCategory.is_active);
    } catch (err) {
      console.error('Failed to load category:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить категорию');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/categories');
  };

  const handleSave = async () => {
    if (!category) return;

    // Validation
    if (!name.trim()) {
      tg?.showAlert('Введите название категории');
      return;
    }
    if (!description.trim()) {
      tg?.showAlert('Введите описание категории');
      return;
    }

    try {
      setSaving(true);

      // Update category
      const updatedCategory = await adminCategoriesApi.updateCategory(category.id, {
        name: name.trim(),
        description: description.trim(),
        is_active: isActive,
      });

      setCategory(updatedCategory);

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }

      tg?.showAlert('Категория успешно обновлена');
    } catch (err: unknown) {
      tg?.showAlert(err instanceof Error ? err.message : 'Ошибка обновления категории');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page admin-category-edit-page">
        <div className="admin-category-edit-header">
          <h1 className="admin-category-edit-title">Редактирование категории</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="page admin-category-edit-page">
        <div className="admin-category-edit-header">
          <h1 className="admin-category-edit-title">Редактирование категории</h1>
        </div>
        <div className="admin-category-edit-error">
          <p className="admin-category-edit-error-message">{error || 'Категория не найдена'}</p>
          <button className="admin-category-edit-retry-btn" onClick={loadCategory}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-category-edit-page">
      <div className="admin-category-edit-header">
        <h1 className="admin-category-edit-title">Редактирование категории</h1>
      </div>

      <div className="admin-category-edit-content">
        {/* Basic Information */}
        <div className="admin-category-edit-section">
          <h2 className="admin-category-edit-section-title">Основная информация</h2>

          <div className="admin-category-edit-form-field">
            <label htmlFor="name">Название *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Пельмени"
              disabled={saving}
            />
          </div>

          <div className="admin-category-edit-form-field">
            <label htmlFor="description">Описание *</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание категории"
              rows={3}
              disabled={saving}
            />
          </div>

          <div className="admin-category-edit-form-field checkbox-field">
            <label htmlFor="is_active" className="checkbox-label">
              <input
                id="is_active"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={saving}
              />
              <span>Категория активна</span>
            </label>
            <span className="admin-category-edit-field-hint">
              Неактивные категории не отображаются в каталоге
            </span>
          </div>
        </div>

        {/* Save Button */}
        <div className="admin-category-edit-actions">
          <button
            className="admin-category-edit-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </div>
  );
};
